import { Combobox } from '@base-ui/react/combobox';
import { Field } from '@base-ui/react/field';
import { Icon, styled } from '@conteg/ui';
import * as React from 'react';

// This ideally should be separated, but as it is POC and we want to avoid too many abstractions, it's kept in the same file for now. It can be easily extracted later if needed.

const StyledFieldRoot = styled(Field.Root)`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;
  position: relative;
  text-align: left;
`;

const FloatingLabel = styled(Field.Label)<{
  $hasValue: boolean;
  $isOpen: boolean;
}>`
  position: absolute;
  left: 3rem;
  top: ${({ $hasValue, $isOpen }) => ($hasValue || $isOpen ? '2rem' : '50%')};
  transform: translateY(
    ${({ $hasValue, $isOpen }) => ($hasValue || $isOpen ? '0' : '-50%')}
  );
  font-size: ${({ $hasValue, $isOpen }) =>
    $hasValue || $isOpen ? '3rem' : '3.5rem'};
  line-height: ${({ $hasValue, $isOpen }) =>
    $hasValue || $isOpen ? '3rem' : '6rem'};
  font-weight: 400;
  color: rgb(117, 117, 117);
  pointer-events: none;
  transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0 1rem;
  z-index: 1;
`;

const StyledTrigger = styled(Combobox.Trigger)`
  width: 100%;
`;

const StyledFieldWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 3rem;
  height: 14rem;
  padding-left: 3.5rem;
  padding-right: 4rem;
  padding-top: 3rem;
  padding-bottom: 3rem;
  margin: 0;
  background: rgba(12, 22, 140, 0.05);
  border: 0.25rem solid transparent;
  border-radius: 3rem;
  font-family: inherit;
  font-size: 4rem;
  line-height: 6rem;
  color: ${({ theme }) => theme.colors.secondary?.grey700 || '#374151'};
  min-width: 150px;
  width: 100%;
  position: relative;

  &:focus-within {
    outline: 2px solid
      ${({ theme }) => theme.colors.primary?.blue500 || '#3b82f6'};
    outline-offset: -1px;
  }

  &:hover {
    background-color: ${({ theme }) =>
      theme.colors.secondary?.grey100 || '#f3f4f6'};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StyledClear = styled(Combobox.Clear)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 6rem;
  height: 6rem;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.secondary?.grey500 || '#6b7280'};
  cursor: pointer;
  border-radius: 1rem;
  transition: all 150ms;

  &:hover {
    background-color: ${({ theme }) =>
      theme.colors.secondary?.grey200 || '#e5e7eb'};
    color: ${({ theme }) => theme.colors.secondary?.grey700 || '#374151'};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme }) => theme.colors.primary?.blue500 || '#3b82f6'};
    outline-offset: 1px;
  }
`;

const ValueContainer = styled.div<{ $hasValue: boolean; $isOpen: boolean }>`
  flex: 1;
  min-width: 0;

  position: relative;
  top: ${({ $hasValue, $isOpen }) => ($hasValue || $isOpen ? '2rem' : '0')};

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const InputContainer = styled.div`
  box-sizing: border-box;
  min-width: 72rem;
  width: 100%;
  height: 12rem;
  text-align: center;
  padding: 2rem;
`;

const StyledInput = styled(Combobox.Input)`
  box-sizing: border-box;
  padding-left: 3.5rem;
  margin: 0;
  border: 1px solid
    ${({ theme }) => theme.colors.secondary?.grey300 || '#d1d5db'};
  min-width: 72rem;
  width: 100%;
  height: 10rem;
  border-radius: 1.5rem;
  font-family: inherit;
  font-size: 4rem;
  background-color: ${({ theme }) => theme.colors.white || '#ffffff'};
  color: ${({ theme }) => theme.colors.secondary?.grey700 || '#374151'};
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary?.blue500 || '#3b82f6'};
    outline: 1px solid
      ${({ theme }) => theme.colors.primary?.blue500 || '#3b82f6'};
  }
`;

const StyledPositioner = styled(Combobox.Positioner)`
  outline: 0;
`;

const StyledPopup = styled(Combobox.Popup)`
  box-sizing: border-box;
  border-radius: 2rem;
  background-color: ${({ theme }) => theme.colors.white || '#ffffff'};
  color: ${({ theme }) => theme.colors.secondary?.grey700 || '#374151'};
  transform-origin: var(--transform-origin);
  transition:
    transform 150ms,
    opacity 150ms;
  max-width: var(--available-width);
  max-height: 96rem;
  outline: 1px solid
    ${({ theme }) => theme.colors.secondary?.grey200 || '#e5e7eb'};
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -4px rgba(0, 0, 0, 0.1);

  &[data-starting-style],
  &[data-ending-style] {
    opacity: 0;
    transform: scale(0.9);
  }
`;

const StyledList = styled(Combobox.List)`
  box-sizing: border-box;
  overflow: auto;
  scroll-padding-block: 2rem;
  padding-block: 2rem;
  overscroll-behavior: contain;
  max-height: min(84rem, calc(var(--available-height) - 12rem));

  &:empty {
    padding: 0;
  }
`;

const StyledItem = styled(Combobox.Item)`
  box-sizing: border-box;
  outline: 0;
  cursor: default;
  user-select: none;
  min-width: var(--anchor-width);
  padding-block: 2rem;
  padding-left: 4rem;
  padding-right: 8rem;
  display: grid;
  gap: 2rem;
  align-items: center;
  grid-template-columns: 3rem 1fr;
  font-size: 4rem;
  line-height: 4rem;

  &[data-highlighted] {
    z-index: 0;
    position: relative;
    color: ${({ theme }) => theme.colors.white || '#ffffff'};
  }

  &[data-highlighted]::before {
    content: '';
    z-index: -1;
    position: absolute;
    inset-block: 0;
    inset-inline: 2rem;
    border-radius: 1rem;
    background-color: ${({ theme }) =>
      theme.colors.secondary?.grey700 || '#374151'};
  }
`;

const ItemText = styled.div`
  grid-column-start: 2;
`;

const StyledItemIndicator = styled(Combobox.ItemIndicator)`
  grid-column-start: 1;
`;

const ItemIndicatorIcon = styled.div`
  display: block;
  width: 3rem;
  height: 3rem;
`;

const StyledEmpty = styled(Combobox.Empty)`
  &:not(:empty) {
    box-sizing: border-box;
    font-size: 3.75rem;
    line-height: 4rem;
    color: ${({ theme }) => theme.colors.secondary?.grey600 || '#4b5563'};
    padding: 4rem;
  }
`;

const StyledChips = styled(Combobox.Chips)`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  flex: 1;
  min-width: 0;
  position: relative;
  top: 2rem;
  overflow-x: auto;
  overflow-y: hidden;

  /* Hide scrollbar for cleaner look */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const StyledChip = styled(Combobox.Chip)`
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 2rem;
  background-color: ${({ theme }) =>
    theme.colors.primary?.blue100 || '#dbeafe'};
  color: ${({ theme }) => theme.colors.primary?.blue700 || '#1d4ed8'};
  border-radius: 1.5rem;
  font-size: 3rem;
  line-height: 4rem;
  white-space: nowrap;
  flex-shrink: 0;
`;

const StyledChipRemove = styled(Combobox.ChipRemove)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary?.blue700 || '#1d4ed8'};
  cursor: pointer;
  width: 3rem;
  height: 3rem;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.primary?.blue700 || '#1d4ed8'};
  }
`;

function normalizeString(str: string) {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export interface ComboboxProps<T> {
  items: T[];
  value?: T | null;
  onChange?: (value: T | null) => void;
  multiValue?: T[];
  onMultiChange?: (values: T[]) => void;
  multiple?: boolean;
  getItemLabel: (item: T | null) => string;
  getItemValue: (item: T) => string | number;
  label: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  required?: boolean;
  disabled?: boolean;
}

export function SelectCombobox<T>({
  items,
  value,
  onChange,
  multiValue = [],
  onMultiChange,
  multiple = false,
  getItemLabel,
  getItemValue,
  label,
  searchPlaceholder,
  emptyMessage,
  required = false,
  disabled = false,
}: ComboboxProps<T>) {
  const [isOpen, setIsOpen] = React.useState(false);
  const hasValue = multiple
    ? multiValue.length > 0
    : value !== null && value !== undefined;

  return (
    <StyledFieldRoot>
      <Combobox.Root
        items={items}
        value={multiple ? multiValue : value}
        onValueChange={(newValue) => {
          if (multiple && onMultiChange) {
            onMultiChange(newValue as T[]);
          } else if (!multiple && onChange) {
            onChange(newValue as T | null);
          }
        }}
        multiple={multiple}
        onOpenChange={setIsOpen}
        disabled={disabled}
        filter={(item, query) => {
          if (!query) return true;
          return normalizeString(getItemLabel(item)).includes(
            normalizeString(query)
          );
        }}
      >
        <StyledTrigger
          nativeButton={false}
          render={(props) => {
            return (
              <div {...props}>
                <FloatingLabel $hasValue={hasValue} $isOpen={isOpen}>
                  {label}
                  {required && ' *'}
                </FloatingLabel>
                <StyledFieldWrapper>
                  {multiple ? (
                    <StyledChips>
                      <Combobox.Value>
                        {(selectedItems: T[]) => (
                          <React.Fragment>
                            {selectedItems.map((item) => (
                              <StyledChip
                                key={getItemValue(item)}
                                aria-label={getItemLabel(item)}
                              >
                                {getItemLabel(item)}
                                <StyledChipRemove aria-label="Remove">
                                  <Icon name="close" />
                                </StyledChipRemove>
                              </StyledChip>
                            ))}
                          </React.Fragment>
                        )}
                      </Combobox.Value>
                    </StyledChips>
                  ) : (
                    <ValueContainer $hasValue={hasValue} $isOpen={isOpen}>
                      <Combobox.Value>
                        {(item: T) => getItemLabel(item)}
                      </Combobox.Value>
                    </ValueContainer>
                  )}
                  <ActionButtons>
                    {hasValue && !multiple && (
                      <StyledClear aria-label="Clear selection">
                        <Icon name="close" />
                      </StyledClear>
                    )}
                    <Combobox.Icon>
                      <Icon name="chevronDown" />
                    </Combobox.Icon>
                  </ActionButtons>
                </StyledFieldWrapper>
              </div>
            );
          }}
        />
        <Combobox.Portal>
          <StyledPositioner align="start" sideOffset={4}>
            <StyledPopup aria-label={label}>
              <InputContainer>
                <StyledInput placeholder={searchPlaceholder} />
              </InputContainer>
              <StyledEmpty>{emptyMessage}</StyledEmpty>
              <StyledList>
                {(item: T) => (
                  <StyledItem key={getItemValue(item)} value={item}>
                    <StyledItemIndicator>
                      <ItemIndicatorIcon>
                        <CheckIcon />
                      </ItemIndicatorIcon>
                    </StyledItemIndicator>
                    <ItemText>{getItemLabel(item)}</ItemText>
                  </StyledItem>
                )}
              </StyledList>
            </StyledPopup>
          </StyledPositioner>
        </Combobox.Portal>
      </Combobox.Root>
    </StyledFieldRoot>
  );
}

function CheckIcon(props: React.ComponentProps<'svg'>) {
  return (
    <svg
      fill="currentcolor"
      width="10"
      height="10"
      viewBox="0 0 10 10"
      {...props}
    >
      <path d="M9.1603 1.12218C9.50684 1.34873 9.60427 1.81354 9.37792 2.16038L5.13603 8.66012C5.01614 8.8438 4.82192 8.96576 4.60451 8.99384C4.3871 9.02194 4.1683 8.95335 4.00574 8.80615L1.24664 6.30769C0.939709 6.02975 0.916013 5.55541 1.19372 5.24822C1.47142 4.94102 1.94536 4.91731 2.2523 5.19524L4.36085 7.10461L8.12299 1.33999C8.34934 0.993152 8.81376 0.895638 9.1603 1.12218Z" />
    </svg>
  );
}
